import { AccountEntityType, newAccountsSteps } from 'constants/new_accounts.constants';
import { formConfig } from 'sections/pendingRequests/forms.types';
import { getAllDays, getAllMonths, getYears } from 'components/FormCreatorComponent/helpers';

import {
	customValueTypes,
	field,
	fieldMode,
	fieldsGroup,
	fieldType,
	fieldsGroupType,
	validationTypes,
} from 'components/FormCreatorComponent/types';
import { countryId } from 'constants/user.constants';
import { getFile } from 'services/global.service';
import { ExpectedPaymentCountriesFormField } from 'sections/NewAccounts/components/ExpectedPaymentCountriesPicker';

const incorporationDateFields = (mode: fieldMode, entityType: AccountEntityType): field[] => {
	const disabled = mode !== fieldMode.write;
	const parts = [
		{ name: 'incorporationDay', label: 'label.day', options: getAllDays() },
		{ name: 'incorporationMonth', label: 'label.month', options: getAllMonths() },
		{ name: 'incorporationYear', label: 'label.year', options: getYears(0) },
	];
	return parts.map((part) => ({
		type: fieldType.select,
		mode,
		props: {
			name: part.name,
			label: part.label,
			options: part.options,
			required: !disabled,
			disabled,
		},
		validation: [{ requierd: true }],
		dependency: { name: 'entityType', value: entityType },
	}));
};

export const incorporationDateGroups = (mode: fieldMode): fieldsGroup[] => [
	{
		type: fieldsGroupType.row,
		title: 'page.accounts.new_account.customer.date_of_incorporation',
		fields: incorporationDateFields(mode, AccountEntityType.CORPORATION),
	},
	{
		type: fieldsGroupType.row,
		title: 'label.birthDate',
		fields: incorporationDateFields(mode, AccountEntityType.COMPANY),
	},
];

export const newAccountTypesForms: formConfig = {
	[newAccountsSteps.CUSTOMER_DETAILS]: [
		{
			name: 'page.managePendingRequests.forms.customer',
			handleChange: () => {},
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'accountNameEnglish',
								label: 'page.accounts.customer_details.accountNameEnglish',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'accountNameHebrew',
								label: 'page.accounts.customer_details.accountNameHebrew',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'hyperAccountNumber',
								label: 'page.accounts.customer_details.hyper_number',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
					],
				},
				{
					type: fieldsGroupType.row,
					fields: [
						{
							type: fieldType.radio,
							mode: fieldMode.readonly,
							props: {
								name: 'entityType',
								label: 'page.accounts.new_account.customer.customer_identification.title',
								options: [
									{ value: 'Legal', label: 'customer.type.individual', disabled: true },
									{ value: 'NotLegal', label: 'customer.type.corporate', disabled: true },
								],
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
					],
				},
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'identityNumber',
								label: 'label.identificationNumber.corporate',
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
							dependency: { name: 'entityType', value: AccountEntityType.CORPORATION },
						},
						{
							type: fieldType.text,
							mode: fieldMode.write,
							props: {
								name: 'identityNumber',
								label: 'label.identificationNumber.company',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
							dependency: { name: 'entityType', value: AccountEntityType.COMPANY },
						},
					],
				},
				...incorporationDateGroups(fieldMode.readonly),
				{
					type: fieldsGroupType.row,
					title: 'page.accounts.new_account.customer.address_hebrew.title',
					fields: [
						{
							type: fieldType.select,
							mode: fieldMode.readonly,
							props: {
								name: 'countryId',
								label: 'label.country',
								options: [],
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'cityHebrew',
								label: 'page.accounts.user_details.city',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'zipCode',
								label: 'label.postalCode',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
					],
				},
				{
					type: fieldsGroupType.row,
					fields: [
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'addressHebrew',
								label: 'page.accounts.customer_details.street_number_hebrew',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
					],
				},
				{
					type: fieldsGroupType.row,
					title: 'page.accounts.new_account.customer.address_english.title',
					fields: [
						{
							type: fieldType.select,
							mode: fieldMode.readonly,
							props: {
								name: 'countryId',
								label: 'label.country',
								options: [],
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'cityEnglish',
								label: 'page.accounts.user_details.city',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'zipCode',
								label: 'label.postalCode',
								disabled: true,
								required: true,
							},
							validation: [{ requierd: true }],
						},
					],
				},
				{
					type: fieldsGroupType.row,
					fields: [
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'addressEnglish',
								label: 'page.accounts.customer_details.street_number_english',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
					],
				},
				{
					type: fieldsGroupType.col,
					title: 'page.accounts.new_account.customer.business_profile.title',
					fields: [
						{
							type: fieldType.select,
							mode: fieldMode.readonly,
							ListboxProps: { dir: 'rtl' },
							props: {
								name: 'industryId',
								label: 'page.accounts.new_account.customer.industry',
								options: [],
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.custom,
							mode: fieldMode.readonly,
							props: {
								name: 'businessWebsiteUrl',
								label: 'page.accounts.new_account.customer.business_website',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
					],
				},
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'maxForPayment',
								label: 'label.maxForPayment',
								disabled: true,
							},
							suffix: '$',
							validation: [{ requierd: true }],
							customValueType: customValueTypes.integer,
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'maxForConvert',
								label: 'label.maxForConvert',
								disabled: true,
							},
							suffix: '$',
							validation: [{ requierd: true }],
							customValueType: customValueTypes.integer,
						},
					],
				},
				{
					type: fieldsGroupType.col,
					title: 'page.accounts.new_account.customer.expected_activity.title',
					fields: [
						{
							type: fieldType.select,
							mode: fieldMode.readonly,
							ListboxProps: { dir: 'rtl' },
							props: {
								name: 'expectedAnnualPaymentsVolume',
								label: 'page.accounts.new_account.customer.expected_annual_payments_volume',
								options: [],
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.select,
							mode: fieldMode.readonly,
							ListboxProps: { dir: 'rtl' },
							props: {
								name: 'expectedMonthlyPaymentsCount',
								label: 'page.accounts.new_account.customer.expected_monthly_payments_count',
								options: [],
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.custom,
							mode: fieldMode.readonly,
							props: {
								name: 'expectedPaymentCountryIds',
								label: 'page.accounts.new_account.customer.expected_payment_countries',
								required: true,
								disabled: true,
								customComponent: ExpectedPaymentCountriesFormField,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.select,
							mode: fieldMode.readonly,
							ListboxProps: { dir: 'rtl' },
							props: {
								name: 'expectedPaymentCurrencyId',
								label: 'page.accounts.new_account.customer.expected_payment_currency',
								options: [],
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
					],
				},
				{
					type: fieldsGroupType.row,
					title: 'page.accounts.new_account.customer.client_risk_level',
					fields: [
						{
							type: fieldType.select,
							mode: fieldMode.readonly,
							ListboxProps: { dir: 'rtl' },
							props: {
								name: 'riskLevel',
								label: 'page.accounts.new_account.customer.client_risk_level',
								options: [
									{ value: 'low', label: 'נמוך' },
									{ value: 'medium', label: 'בינוני' },
									{ value: 'high', label: 'גבוהה' },
									{ value: 'veryHigh', label: 'גבוהה מאוד' },
								],
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
					],
				},
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.select,
							mode: fieldMode.readonly,
							ListboxProps: { dir: 'rtl' },
							props: {
								name: 'customerSuccessManagerId',
								label: 'page.customerAccounts.basicData.manager',
								options: [],
								required: false,
								disabled: true,
							},
						},
						{
							type: fieldType.select,
							mode: fieldMode.readonly,
							ListboxProps: { dir: 'rtl' },
							props: {
								name: 'agentId',
								label: 'page.customerAccounts.basicData.agent',
								options: [],
								required: false,
								disabled: true,
							},
						},
						{
							type: fieldType.select,
							mode: fieldMode.readonly,
							ListboxProps: { dir: 'rtl' },
							props: {
								name: 'dealerId',
								label: 'page.customerAccounts.basicData.dealer',
								options: [],
								required: false,
								disabled: true,
							},
						},
						{
							type: fieldType.select,
							mode: fieldMode.readonly,
							ListboxProps: { dir: 'rtl' },
							props: {
								name: 'leadId',
								label: 'page.customerAccounts.basicData.lead',
								options: [],
								required: false,
								disabled: true,
							},
						},
					],
				},
			],
		},
	],
	[newAccountsSteps.USER_DETAILS]: [
		{
			name: 'page.managePendingRequests.forms.account',
			handleChange: () => {},
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'firstNameHebrew',
								label: 'label.firstName',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'lastNameHebrew',
								label: 'label.lastName',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'firstNameEnglish',
								label: 'label.firstNameEnglish',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'lastNameEnglish',
								label: 'label.lastNameEnglish',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
					],
				},
				{
					type: fieldsGroupType.row,
					title: 'label.birthDate',
					fields: [
						{
							type: fieldType.select,
							mode: fieldMode.readonly,
							props: {
								name: 'day',
								label: 'label.day',
								options: getAllDays(),
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.select,
							mode: fieldMode.readonly,
							props: {
								name: 'month',
								label: 'label.month',
								options: getAllMonths(),
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.select,
							mode: fieldMode.readonly,
							props: {
								name: 'year',
								label: 'label.year',
								options: getYears(),
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
					],
				},
				{
					type: fieldsGroupType.row,
					fields: [
						{
							type: fieldType.radio,
							mode: fieldMode.readonly,
							props: {
								name: 'legalStatus',
								label: 'label.legalStatus',
								options: [
									{ value: 'Legal', label: 'label.legal', disabled: true },
									{ value: 'NotLegal', label: 'label.illegal', disabled: true },
								],
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
					],
				},
				{
					type: fieldsGroupType.row,
					fields: [
						{
							type: fieldType.radio,
							mode: fieldMode.readonly,
							props: {
								name: 'identificationType',
								label: 'label.identificationType',
								options: [
									{ value: 'ID', label: 'label.identicationId', disabled: true },
									{ value: 'Passport', label: 'label.Passport', disabled: true },
								],
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
					],
				},
				{
					type: fieldsGroupType.row,
					fields: [
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'identificationNumber',
								label: 'page.accounts.new_account.customer.customer_identification.title',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
					],
				},
				/*
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.radio,
							mode: fieldMode.readonly,
							props: {
								name: 'gender',
								label: 'label.gender',
								options: [
									{ value: 'Male', label: 'label.male', disabled: true },
									{ value: 'Female', label: 'label.female', disabled: true },
								],
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
					],
				},
				*/
			],
		},
		{
			name: 'page.managePendingRequests.forms.contact',
			handleChange: () => {},
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'emailAddress',
								label: 'label.email',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }, { email: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'phoneNumber',
								label: 'label.phone',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }, { phone: true }],
						},
						/*
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'address',
								label: 'label.address',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'city',
								label: 'label.city',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.select,
							mode: fieldMode.readonly,
							props: {
								name: 'countryId',
								label: 'label.country',
								required: true,
								disabled: false,
								options: [{ value: countryId, label: 'Israel' }],
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.select,
							mode: fieldMode.readonly,
							props: {
								name: 'birthCountryId',
								label: 'label.birthCountryId',
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
						*/
					],
				},
			],
		},
		{
			name: 'page.managePendingRequests.forms.manageAccountDetails',
			handleChange: () => {},
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'role',
								label: 'label.role',
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.select,
							mode: fieldMode.readonly,
							props: {
								name: 'permission',
								label: 'label.permisionLevel',
								options: [{ label: '', value: '1' }],
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'maxForPayment',
								label: 'label.maxForPayment',
								disabled: true,
							},
							suffix: '$',
							validation: [{ requierd: true }],
							customValueType: customValueTypes.integer,
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'maxForConvert',
								label: 'label.maxForConvert',
								disabled: true,
							},
							suffix: '$',
							validation: [{ requierd: true }],
							customValueType: customValueTypes.integer,
						},
					],
				},
			],
		},
	],
	[newAccountsSteps.BANK_ACCOUNT_DETAILS]: [
		{
			name: 'page.managePendingRequests.forms.bank',
			handleChange: () => {},
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'accountOwnerName',
								label: 'page.pendingRequests.field.accountOwnerName',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.select,
							mode: fieldMode.readonly,
							props: {
								name: 'bankId',
								label: 'page.pendingRequests.field.bankName',
								options: [],
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'bankBranch',
								label: 'page.pendingRequests.field.bankBranch',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readWithEnableWrite,
							props: {
								name: 'accountNumber',
								label: 'page.pendingRequests.field.accountNumber',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
					],
				},
			],
		},
		{
			name: 'page.managePendingRequests.forms.approveAccount',
			handleChange: () => {},
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.file,
							mode: fieldMode.readWithEnableWrite,
							props: {
								name: 'file',
								label: 'page.pendingRequests.field.fileReference',
								required: true,
								disabled: true,
								onClick: (file) => {
									getFile(file?.key);
								},
							},
							validation: [{ requierd: true }],
						},
					],
				},
			],
		},
	],
};
